import React from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Target } from 'lucide-react';
import AccountSummary from './components/AccountSummary';
import InvestorAccountSummary from './components/InvestorAccountSummary';
import InvestorQuickStart from './components/InvestorQuickStart';
import InvestorStrategyView from './components/InvestorStrategyView';
import LogViewer from './components/LogViewer';
import ManualOrderEntry from './components/ManualOrderEntry';
import PositionsOrdersPanel from './components/PositionsOrdersPanel';
import SignalLogPanel from './components/SignalLogPanel';
import StrategyConfigPanel from './components/StrategyConfigPanel';
import StrategyStatus from './components/StrategyStatus';
import type { Order, SystemStatus } from './services/api';
import { isDevelopmentBuild } from './utils/buildEnvironment';

const ControlPage = React.lazy(() => import('./pages/ControlPage'));
const TradeLogPage = React.lazy(() => import('./pages/TradeLogPage'));
const Backtester = React.lazy(() => import('./pages/Backtester'));
const OptimizationResultsPage = React.lazy(() => import('./pages/OptimizationResultsPage'));
const BacktestDashboard = React.lazy(() => import('./pages/BacktestDashboard'));
const SharedBacktestsPage = React.lazy(() => import('./pages/SharedBacktestsPage'));
const DataPage = React.lazy(() => import('./pages/DataPage'));
const PositionsPage = React.lazy(() => import('./pages/PositionsPage'));
const ManagedORBPage = React.lazy(() => import('./pages/ManagedORBPage'));
const PortfolioPage = React.lazy(() => import('./pages/PortfolioPage'));
const PortfolioHistoryPage = React.lazy(() => import('./pages/PortfolioHistoryPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const AdminLogsPage = React.lazy(() => import('./pages/AdminLogsPage'));
const DevComponentsPage = React.lazy(() => import('./pages/DevComponentsPage'));

const pageLoadingFallback = (
  <div className="flex min-h-[40vh] items-center justify-center" role="status">
    <span className="text-sm text-muted">Loading page…</span>
  </div>
);

interface AppRoutesProps {
  user: { id?: number; role?: string } | null;
  userContext: { role?: string; is_superuser?: boolean } | null;
  systemStatus: SystemStatus | null;
  editingOrder: Order | null;
  onEditingOrderChange: (order: Order | null) => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  user,
  userContext,
  systemStatus,
  editingOrder,
  onEditingOrderChange,
}) => (
  <React.Suspense fallback={pageLoadingFallback}>
    <Routes>
      <Route path="/" element={
        <div className="flex min-h-[calc(100vh-5rem)] w-full flex-col gap-0 px-0 py-0">
          {user?.role === 'investor' && <InvestorQuickStart />}

          <div className="grid grid-cols-1 gap-0 lg:grid-cols-3 lg:auto-rows-fr">
            <div className={`flex flex-col gap-0 lg:h-full ${user?.role === 'investor' ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
              {user?.role === 'investor' ? (
              <InvestorAccountSummary isConnected={!!systemStatus?.data?.ib_connected} />
              ) : (
                <AccountSummary />
              )}
            </div>
            {user?.role !== 'investor' && (
              <div className="flex flex-col gap-0 lg:h-full lg:border-l lg:border-white/10">
                <ManualOrderEntry
                  editingOrder={editingOrder}
                  onCancelEditing={() => onEditingOrderChange(null)}
                  onOrderModified={() => onEditingOrderChange(null)}
                />
              </div>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-0">
            <PositionsOrdersPanel className="min-h-[560px] flex-1" onEditOrder={onEditingOrderChange} />
            {user?.role === 'investor' ? <InvestorStrategyView /> : <StrategyStatus />}
          </div>

          {user?.role !== 'investor' && <LogViewer />}
        </div>
      } />
      <Route path="/control" element={<ControlPage />} />
      <Route path="/trades" element={user?.role === 'investor' ? <Navigate to="/" /> : <TradeLogPage />} />
      <Route path="/analysis" element={<Navigate to="/" replace />} />
      <Route path="/strategies" element={
        user?.role === 'investor' ? <Navigate to="/" /> : (
          <div className="flex min-h-[calc(100vh-5rem)] w-full flex-col gap-0 px-0 py-0">
            <div className="panel full-bleed-panel page-heading">
              <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-white">
                <Target className="h-5 w-5 text-neon" />
                Strategies
              </h1>
              <p className="text-sm text-muted">Live status, monthly momentum rankings, and settings for ON, ORB, DT, and NQMM. Managed ORB has a dedicated workspace.</p>
            </div>
            <StrategyStatus />

            <div className="panel full-bleed-panel">
              <div className="page-heading">
                <h2 className="text-lg font-bold tracking-tight text-white">Configuration</h2>
              </div>
              <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
                <StrategyConfigPanel strategyName="DT" fullBleed />
                <StrategyConfigPanel strategyName="ORB" fullBleed />
                <StrategyConfigPanel strategyName="ON" fullBleed />
                <StrategyConfigPanel strategyName="NQMM" fullBleed />
              </div>
            </div>

            <div className="lg:col-span-1 border-t divider-line pt-0">
              <SignalLogPanel fullBleed />
            </div>
          </div>
        )
      } />
      <Route path="/backtest" element={user?.role === 'investor' ? <SharedBacktestsPage key={user?.id} /> : <BacktestDashboard key={user?.id} />} />
      <Route
        path="/data"
        element={user?.role === 'investor'
          ? <Navigate to="/" />
          : <DataPage canManageSharedMarketData={
              userContext?.role === 'admin' || userContext?.is_superuser === true
            } />}
      />
      <Route path="/positions" element={user?.role === 'investor' ? <Navigate to="/" /> : <PositionsPage />} />
      <Route path="/managed-orb" element={user?.role === 'investor' ? <Navigate to="/" /> : <ManagedORBPage />} />
      <Route path="/backtest/:id" element={user?.role === 'investor' ? <SharedBacktestsPage key={user?.id} /> : <Backtester key={user?.id} />} />
      <Route path="/backtest/strategy/:strategyId" element={user?.role === 'investor' ? <Navigate to="/" /> : <Backtester key={user?.id} />} />
      <Route path="/portfolio" element={user?.role === 'investor' ? <Navigate to="/" /> : <PortfolioPage />} />
      <Route path="/portfolio/history" element={user?.role === 'investor' ? <Navigate to="/" /> : <PortfolioHistoryPage />} />
      <Route path="/optimization/:id" element={user?.role === 'investor' ? <Navigate to="/" /> : <OptimizationResultsPage />} />
      <Route path="/admin" element={user?.role === 'admin' ? <AdminPage /> : <Navigate to="/" />} />
      <Route path="/admin/logs" element={user?.role === 'admin' ? <AdminLogsPage /> : <Navigate to="/" />} />
      <Route path="/dev/components" element={isDevelopmentBuild ? <DevComponentsPage /> : <Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </React.Suspense>
);

export default AppRoutes;
